/**
 * Modal: Agendamento múltiplo de limpezas
 * ─────────────────────────────────────────
 * Lets the host/admin schedule N cleanings at once for the same apartment.
 * Each row carries date, time, type, optional client_name, optional notes.
 *
 * On save, posts to `/admin/bookings/bulk` and surfaces per-row errors so
 * the user can fix only the failing rows without losing the others.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Save, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toLocalIso } from "../lib/dateUtils";

const APT_TYPES = ["T0", "T1", "T2", "T3", "T4", "T5", "VS", "VM", "VL", "HOTEL"];
const TIME_SLOTS = [
  "08:00", "09:00", "10:00", "11:00", "12:00", "13:00",
  "14:00", "15:00", "16:00", "17:00", "18:00",
];

function newRow(defaults = {}) {
  return {
    uid: crypto.randomUUID(),
    scheduled_date: "",
    scheduled_time: "10:00",
    apt_type: defaults.apt_type || "T1",
    guest_name: "",
    guest_phone: "",
    notes: "",
    error: null, // populated after save attempt
  };
}

export default function BulkBookingModal({
  open, apartmentId: initialApartmentId, onClose, onSuccess,
}) {
  const [apartments, setApartments] = useState([]);
  const [apartmentId, setApartmentId] = useState(initialApartmentId || "");
  const [rows, setRows] = useState([newRow()]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // {created, failed, ok_count, fail_count}

  useEffect(() => {
    if (!open) return;
    api.get("/apartments")
      .then(({ data }) => setApartments(data.items || []))
      .catch(() => {});
    setRows([newRow()]);
    setResult(null);
    if (initialApartmentId) setApartmentId(initialApartmentId);
  }, [open, initialApartmentId]);

  const selectedApt = useMemo(
    () => apartments.find(a => a.id === apartmentId),
    [apartments, apartmentId],
  );

  // When the apartment changes, prefill the apt_type column on empty rows
  // (don't overwrite user choices).
  useEffect(() => {
    if (!selectedApt) return;
    setRows(rs => rs.map(r => (
      r.scheduled_date || r.guest_name ? r : { ...r, apt_type: selectedApt.apt_type || r.apt_type }
    )));
  }, [selectedApt]);

  function patch(uid, p) {
    setRows(rs => rs.map(r => r.uid === uid ? { ...r, ...p, error: null } : r));
  }
  function addRow() {
    setRows(rs => [...rs, newRow({ apt_type: selectedApt?.apt_type })]);
  }
  function removeRow(uid) {
    setRows(rs => rs.length === 1 ? rs : rs.filter(r => r.uid !== uid));
  }
  function duplicateRow(uid) {
    setRows(rs => {
      const idx = rs.findIndex(r => r.uid === uid);
      if (idx < 0) return rs;
      const base = rs[idx];
      const copy = newRow({ apt_type: base.apt_type });
      copy.scheduled_time = base.scheduled_time;
      copy.guest_name = base.guest_name;
      copy.notes = base.notes;
      // increment date by +1 day for fast sequential typing
      if (base.scheduled_date) {
        const d = new Date(`${base.scheduled_date}T00:00:00`);
        d.setDate(d.getDate() + 1);
        copy.scheduled_date = toLocalIso(d);
      }
      return [...rs.slice(0, idx + 1), copy, ...rs.slice(idx + 1)];
    });
  }

  function validate() {
    if (!apartmentId) return "Escolha um alojamento.";
    for (const r of rows) {
      if (!r.scheduled_date) return "Cada linha precisa de uma data.";
      if (!r.scheduled_time) return "Cada linha precisa de uma hora.";
      if (!APT_TYPES.includes(r.apt_type)) return "Tipologia inválida.";
    }
    // detect duplicate date+time (same apartment)
    const seen = new Set();
    for (const r of rows) {
      const k = `${r.scheduled_date}|${r.scheduled_time}`;
      if (seen.has(k)) return `Linha duplicada: ${k}.`;
      seen.add(k);
    }
    return null;
  }

  async function save() {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    setResult(null);
    try {
      const payload = {
        apartment_id: apartmentId,
        rows: rows.map(r => ({
          scheduled_date: r.scheduled_date,
          scheduled_time: r.scheduled_time,
          apt_type: r.apt_type,
          guest_name: r.guest_name || undefined,
          guest_phone: r.guest_phone || undefined,
          notes: r.notes || undefined,
        })),
      };
      const { data } = await api.post("/admin/bookings/bulk", payload);
      setResult(data);
      // mark rows with errors so the user sees what to fix
      if (data.fail_count > 0) {
        const errByIdx = new Map(data.failed.map(f => [f.index, f.error]));
        setRows(rs => rs.map((r, idx) => ({ ...r, error: errByIdx.get(idx) || null })));
        toast.warning(`${data.ok_count} criadas, ${data.fail_count} com erro.`);
      } else {
        toast.success(`${data.ok_count} agendamento${data.ok_count > 1 ? "s" : ""} criado${data.ok_count > 1 ? "s" : ""} com sucesso.`);
        if (onSuccess) onSuccess(data);
        // Auto-close after a short pause so the user can read the success
        setTimeout(() => onClose && onClose(), 800);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
         data-testid="bulk-booking-modal">
      <div className="lux-card w-full md:max-w-5xl max-h-[95vh] flex flex-col bg-[#041f1b] border-[#d4af37]/30">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#1a4640] flex items-baseline justify-between gap-3">
          <div>
            <div className="eyebrow text-[#d4af37]">Agendamento múltiplo</div>
            <h2 className="font-display text-2xl mt-1">Várias limpezas, mesmo alojamento</h2>
            <p className="text-xs text-[#a0b2af] mt-1">
              Adicione todas as datas em que precisa de limpeza neste alojamento.
              Pode editar o cliente, tipologia e notas linha a linha.
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-[#a0b2af] hover:text-white"
                  data-testid="bulk-booking-close">
            <X size={20} />
          </button>
        </div>

        {/* Apartment picker */}
        <div className="px-6 py-4 border-b border-[#1a4640]">
          <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Alojamento</label>
          <select
            value={apartmentId}
            onChange={e => setApartmentId(e.target.value)}
            disabled={!!initialApartmentId}
            className="w-full mt-1 bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-3 py-2 text-sm outline-none disabled:opacity-60"
            data-testid="bulk-booking-apartment">
            <option value="">— Escolher alojamento —</option>
            {apartments.map(a => (
              <option key={a.id} value={a.id}>{a.name} · {a.apt_type}</option>
            ))}
          </select>
        </div>

        {/* Rows table */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Desktop header */}
          <div className="hidden md:grid grid-cols-[110px_90px_70px_1fr_1fr_30px] gap-2 pb-2 border-b border-[#1a4640] text-[10px] uppercase tracking-widest text-[#a0b2af]">
            <div>Data</div><div>Hora</div><div>Tipo</div><div>Cliente / Hóspede</div><div>Notas</div><div></div>
          </div>
          <div className="divide-y divide-[#1a4640]">
            {rows.map((r, idx) => (
              <div key={r.uid}
                   className={`py-3 grid grid-cols-2 md:grid-cols-[110px_90px_70px_1fr_1fr_30px] gap-2 ${r.error ? "bg-[#ef4444]/5" : ""}`}
                   data-testid={`bulk-row-${idx}`}>
                <input type="date"
                       value={r.scheduled_date}
                       onChange={e => patch(r.uid, { scheduled_date: e.target.value })}
                       className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1.5 text-sm outline-none"
                       data-testid={`bulk-date-${idx}`} />
                <select value={r.scheduled_time}
                        onChange={e => patch(r.uid, { scheduled_time: e.target.value })}
                        className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1.5 text-sm outline-none"
                        data-testid={`bulk-time-${idx}`}>
                  {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={r.apt_type}
                        onChange={e => patch(r.uid, { apt_type: e.target.value })}
                        className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1.5 text-sm outline-none"
                        data-testid={`bulk-type-${idx}`}>
                  {APT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input type="text" maxLength={120}
                       value={r.guest_name}
                       onChange={e => patch(r.uid, { guest_name: e.target.value })}
                       placeholder={selectedApt?.name ? `(deixar vazio = ${selectedApt.name})` : "Nome do cliente / hóspede"}
                       className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1.5 text-sm outline-none col-span-2 md:col-span-1"
                       data-testid={`bulk-guest-${idx}`} />
                <input type="text" maxLength={200}
                       value={r.notes}
                       onChange={e => patch(r.uid, { notes: e.target.value })}
                       placeholder="Notas (opcional)"
                       className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1.5 text-sm outline-none col-span-2 md:col-span-1"
                       data-testid={`bulk-notes-${idx}`} />
                <div className="flex items-center justify-end gap-1">
                  <button onClick={() => duplicateRow(r.uid)}
                          className="p-1 text-[#a0b2af] hover:text-[#d4af37]"
                          title="Duplicar (data +1)"
                          data-testid={`bulk-duplicate-${idx}`}>
                    <Plus size={14} />
                  </button>
                  {rows.length > 1 && (
                    <button onClick={() => removeRow(r.uid)}
                            className="p-1 text-[#a0b2af] hover:text-[#ef4444]"
                            data-testid={`bulk-remove-${idx}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                {r.error && (
                  <div className="col-span-full text-[11px] text-[#fca5a5] flex items-center gap-1.5"
                       data-testid={`bulk-error-${idx}`}>
                    <AlertTriangle size={12} /> {r.error}
                  </div>
                )}
              </div>
            ))}
          </div>

          <button onClick={addRow}
                  className="mt-3 btn-ghost-gold w-full py-2 text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2"
                  data-testid="bulk-add-row">
            <Plus size={14} /> Adicionar outra data
          </button>

          {result && (
            <div className={`mt-4 lux-card p-3 ${result.fail_count === 0 ? "border-[#10b981]/40 bg-[#10b981]/5" : "border-[#f59e0b]/40 bg-[#f59e0b]/5"}`}
                 data-testid="bulk-result">
              <div className="flex items-center gap-2 text-sm">
                {result.fail_count === 0
                  ? <CheckCircle2 size={16} className="text-[#10b981]" />
                  : <AlertTriangle size={16} className="text-[#f59e0b]" />}
                <strong>{result.ok_count}</strong> agendamento(s) criado(s){result.fail_count > 0 ? `, ${result.fail_count} com erro` : ""}.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#1a4640] flex items-center justify-between gap-3">
          <div className="text-xs text-[#a0b2af]">
            {rows.length} {rows.length === 1 ? "linha" : "linhas"} · alterações guardadas só ao clicar em <strong>Salvar</strong>.
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
                    className="px-4 py-2 text-sm text-[#a0b2af] hover:text-white"
                    data-testid="bulk-booking-cancel">
              Cancelar
            </button>
            <button onClick={save} disabled={saving}
                    className="btn-gold px-5 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="bulk-booking-save">
              <Save size={14} />
              {saving ? "A guardar…" : `Salvar ${rows.length} agendamento${rows.length > 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
